import {
  COMMON_ERR_NETWORK,
  COMMON_ERR_NO_PERMISSION,
  COMMON_MESSAGE_ERROR,
} from '@constants/common';
import { EUserAccessRoles, EUserRoles } from '@models/user';
import { DatePicker, type GetProps } from 'antd';
import { SortOrder } from 'antd/lib/table/interface';
import dayjs from 'dayjs';
import { EHttpStatusCode, EOrder } from 'types/service';
import Cookies from 'universal-cookie';
import { appToast } from './appToast';
import { t } from 'i18next';
import { COMMON_CONSTANT, USER_ROLE } from './constants';

type RangePickerProps = GetProps<typeof DatePicker.RangePicker>;

const cookies = new Cookies();

// Disable date calendar
export const handleDisabledDate: RangePickerProps['disabledDate'] = (
  current
) => {
  // Get yesterday
  const yesterday = dayjs().subtract(1, 'day').startOf('day');

  // Get the date one year ago from yesterday
  const oneYearAgo = yesterday.subtract(1, 'year');

  // Can not select days after today and today
  // Can not select over 1 year from previous day
  return (
    current &&
    (current >= dayjs().startOf('day') ||
      current > yesterday ||
      current < oneYearAgo)
  );
};

/**
 * Remove all saved cookies
 */
export const removeAllCookies = () => {
  const allCookies = Object.getOwnPropertyNames(cookies.getAll());
  for (let i = 0; i < allCookies.length; i++) {
    cookies.remove(allCookies[i], { path: '/' });
  }
};

// SECURITY: mapping này quyết định quyền truy cập toàn app (route-guard AuthRoute +
// sidebar menu LayoutAdmin) — PHẢI fail-closed (role lạ → quyền thấp nhất), KHÔNG
// được fail-open (role lạ → ADMIN). Round 3 (PC009/UTC-TC068) đã phát hiện + xoá bỏ
// fallback debug cũ `return EUserAccessRoles.ADMIN;` không điều kiện — xem
// vams_docs/Tasks/PC009/bug-fix.md F6.
//
// BE (vams_backend_api/src/vams.Core/Constants/Constant.cs, struct `Roles` +
// migration 20260722024857_UpdateDataInTableRole.cs) có 3 role thật: "ADMIN",
// "STORE_OWNER" và "AREA_OWNER" (地区長, thêm sau — migration
// 20260930000000_AddAreaOwnerRoleAndSectorToUsers) — khớp trực tiếp
// EUserRoles.ADMIN/STORE_OWNER/AREA_OWNER.
export const getAccessRoleFromUserRole = (userRole?: EUserRoles[]) => {
  // So sánh theo string thật (không chỉ theo enum) vì `roleCode` từ BE được cast
  // "as EUserRoles" ở nơi gọi (AuthRoute.tsx/LayoutAdmin.tsx) mà không qua validate —
  // giá trị runtime có thể không khớp bất kỳ literal nào của enum EUserRoles.
  const roleCode = userRole?.[0] as unknown as string | undefined;

  // Admin
  if (roleCode === EUserRoles.ADMIN) {
    return EUserAccessRoles.ADMIN;
  }
  // Area owner (地区長) — scoped to their own sector's stores (enforced backend-side).
  if (roleCode === EUserRoles.AREA_OWNER) {
    return EUserAccessRoles.AREA_OWNER;
  }
  // Store owner
  if (roleCode === EUserRoles.STORE_OWNER) {
    return EUserAccessRoles.STORE_OWNER;
  }

  // Fail-closed: roleCode lạ/không xác định được → KHÔNG cấp ADMIN (nguyên tắc
  // deny-by-default). Trả về access-role thấp nhất/ít đặc quyền nhất hiện có
  // (STORE_OWNER), không phải ADMIN — tránh lặp lại lỗi bảo mật đã xảy ra ở round 2.
  return EUserAccessRoles.STORE_OWNER;
};

// Convert SortOrder of antd to map with api
export const getSortDirection = (tableSortOrder?: SortOrder) => {
  switch (tableSortOrder) {
    case 'ascend': {
      return EOrder.ASC;
    }
    case 'descend': {
      return EOrder.DESC;
    }
    default:
      return null;
  }
};

// Generate file name when export list shop/supplier/maker
export const genFileNameForExport = ({
  prefixName,
  companyName,
  storeName,
  from,
  to,
}: {
  prefixName: string;
  companyName: string;
  storeName: string;
  from: string;
  to: string;
}) => {
  if (to) {
    return `${prefixName}_${companyName}会社_${storeName}_${from}_${to}`;
  }
  return `${prefixName}_${companyName}会社_${from}`;
};

// Format asset number: insert a dash before the last 3 characters (e.g. "20527001" → "20527-001").
export const formatAssetNo = (value?: string | null): string => {
  if (!value || value.length <= 3) {
    return value ?? '';
  }
  return `${value.slice(0, -3)}-${value.slice(-3)}`;
};

// Handle generate random ID
export const guidGenerator = () => {
  const S4 = function () {
    return (((1 + Math.random()) * 0x10000) | 0).toString(16).substring(1);
  };
  return (
    S4() +
    S4() +
    '-' +
    S4() +
    '-' +
    S4() +
    '-' +
    S4() +
    '-' +
    S4() +
    S4() +
    S4()
  );
};

export function convertUserListRole(role: string) {
  switch (role) {
    case USER_ROLE.ADMIN:
      return '1' + '_' + t('ROLE_NAME.ADMIN');
    case USER_ROLE.STORE_OWNER:
      return '2' + '_' + t('ROLE_NAME.STORE_OWNER');
    case USER_ROLE.AREA_OWNER:
      return '3' + '_' + t('ROLE_NAME.AREA_OWNER');
    default:
      return '';
  }
}

// Http status codes that should be treated as a generic connection/server error
const COMMON_ERROR_HTTP_STATUS_CODES = [
  EHttpStatusCode.INTERNAL_SERVER_ERROR,
  // EHttpStatusCode.BAD_REQUEST,
  EHttpStatusCode.FORBIDDEN,
  EHttpStatusCode.NOT_FOUND,
  EHttpStatusCode.BAD_GATEWAY,
  EHttpStatusCode.SERVICE_UNAVAILABLE,
  EHttpStatusCode.UNAUTHORIZED,
];

// Error disconnect internet or server error
export const handleShowToastError = (error: any, pageTitle?: string) => {
  const isNetworkError = error === COMMON_ERR_NETWORK;
  const isCommonErrorStatus = COMMON_ERROR_HTTP_STATUS_CODES.includes(
    error.status
  );
  if (isNetworkError || isCommonErrorStatus) {
    appToast.error({ message: COMMON_MESSAGE_ERROR });
    return true;
  }
  if (error === COMMON_ERR_NO_PERMISSION) {
    appToast.error({
      message: t('COMMON_MSG.NO_PERMISSION_COMMON_MSG').replace(
        '{0}',
        pageTitle || ''
      ),
    });
    return true;
  }
  if (error === 'CameraNotConnected') {
    appToast.error({ message: '利用可能なカメラ情報がありません。' });
    return true;
  }
  return false;
};

// Handle import wrong file
export function handleImportWrongFile(error: any) {
  if (error.status === EHttpStatusCode.BAD_REQUEST && error?.data?.message) {
    appToast.error({ message: 'Upload wrong file' });
  }
}

// Handle error code import file
export function handleErrorCodeImportCompanyFile(
  error: any
): string | undefined {
  const errorCode = error?.data?.errors?.[0]?.code;
  if (!errorCode) {
    return undefined;
  } else {
    switch (errorCode) {
      case COMMON_CONSTANT.IMPORT_MASTER_DATA_ERROR_CODE.COMPANY_DUPLICATE:
        return COMMON_CONSTANT.IMPORT_MASTER_DATA_MESSAGE.COMMON_DUPLICATE_MSG;
      case COMMON_CONSTANT.IMPORT_MASTER_DATA_ERROR_CODE
        .COMPANY_ALREADY_EXISTED:
        return COMMON_CONSTANT.IMPORT_MASTER_DATA_MESSAGE.COMMON_ALREADY_EXISTED_MSG.replace(
          '{0}',
          `「${error.data.errors?.[0]?.columnName}」`
        );
      case COMMON_CONSTANT.IMPORT_MASTER_DATA_ERROR_CODE.COMPANY_INVALID_FORMAT:
        return COMMON_CONSTANT.IMPORT_MASTER_DATA_MESSAGE.COMMON_INVALID_FORMAT_MSG.replace(
          '{0}',
          `「${error.data.errors?.[0]?.columnName}」`
        );
      case COMMON_CONSTANT.IMPORT_MASTER_DATA_ERROR_CODE
        .COMMON_COLUMN_NOT_CORRECT:
        return COMMON_CONSTANT.IMPORT_MASTER_DATA_MESSAGE.COMMON_COLUMN_NOT_CORRECT_MSG.replace(
          '{0}',
          `「${error.data.errors?.[0]?.columnName}」`
        );
      case COMMON_CONSTANT.IMPORT_MASTER_DATA_ERROR_CODE
        .COMMON_IMPORT_EMPTY_FILE:
        return COMMON_CONSTANT.IMPORT_MASTER_DATA_MESSAGE
          .COMMON_IMPORT_EMPTY_FILE_MSG;
      case COMMON_CONSTANT.IMPORT_MASTER_DATA_ERROR_CODE.COMPANY_DATA_MISSING:
        return COMMON_CONSTANT.IMPORT_MASTER_DATA_MESSAGE.COMMON_DATA_MISSING_MSG.replace(
          '{0}',
          `「${error.data.errors?.[0]?.columnName}」`
        );
      default:
        return undefined;
    }
  }
}

export function handleErrorCodeImportSectorFile(
  error: any
): string | undefined {
  const errorCode = error?.data?.errors?.[0]?.code;
  switch (errorCode) {
    case COMMON_CONSTANT.IMPORT_MASTER_DATA_ERROR_CODE.SECTOR_DUPLICATE:
      return COMMON_CONSTANT.IMPORT_MASTER_DATA_MESSAGE.COMMON_DUPLICATE_MSG;
    case COMMON_CONSTANT.IMPORT_MASTER_DATA_ERROR_CODE.SECTOR_NOT_EXISTED:
      return COMMON_CONSTANT.IMPORT_MASTER_DATA_MESSAGE.COMMON_NOT_EXISTED_MSG.replace(
        '{0}',
        `「${error.data.errors?.[0]?.columnName}」`
      );
    case COMMON_CONSTANT.IMPORT_MASTER_DATA_ERROR_CODE.SECTOR_INVALID_FORMAT:
      return COMMON_CONSTANT.IMPORT_MASTER_DATA_MESSAGE.COMMON_INVALID_FORMAT_MSG.replace(
        '{0}',
        `「${error.data.errors?.[0]?.columnName}」`
      );
    case COMMON_CONSTANT.IMPORT_MASTER_DATA_ERROR_CODE.SECTOR_ALREADY_EXISTED:
      return COMMON_CONSTANT.IMPORT_MASTER_DATA_MESSAGE.COMMON_ALREADY_EXISTED_MSG.replace(
        '{0}',
        `「${error.data.errors?.[0]?.columnName}」`
      );
    case COMMON_CONSTANT.IMPORT_MASTER_DATA_ERROR_CODE
      .COMMON_COLUMN_NOT_CORRECT:
      return COMMON_CONSTANT.IMPORT_MASTER_DATA_MESSAGE.COMMON_COLUMN_NOT_CORRECT_MSG.replace(
        '{0}',
        `「${error.data.errors?.[0]?.columnName}」`
      );
    case COMMON_CONSTANT.IMPORT_MASTER_DATA_ERROR_CODE.COMMON_IMPORT_EMPTY_FILE:
      return COMMON_CONSTANT.IMPORT_MASTER_DATA_MESSAGE
        .COMMON_IMPORT_EMPTY_FILE_MSG;
    case COMMON_CONSTANT.IMPORT_MASTER_DATA_ERROR_CODE.SECTOR_DATA_MISSING:
      return COMMON_CONSTANT.IMPORT_MASTER_DATA_MESSAGE.COMMON_DATA_MISSING_MSG.replace(
        '{0}',
        `「${error.data.errors?.[0]?.columnName}」`
      );
    default:
      return undefined;
  }
}

export function handleErrorCodeImportAreaFile(error: any): string | undefined {
  const errorCode = error?.data?.errors?.[0]?.code;
  switch (errorCode) {
    case COMMON_CONSTANT.IMPORT_MASTER_DATA_ERROR_CODE.AREA_DUPLICATE:
      return COMMON_CONSTANT.IMPORT_MASTER_DATA_MESSAGE.COMMON_DUPLICATE_MSG;
    case COMMON_CONSTANT.IMPORT_MASTER_DATA_ERROR_CODE.SECTOR_NOT_EXISTED:
      return COMMON_CONSTANT.IMPORT_MASTER_DATA_MESSAGE.COMMON_NOT_EXISTED_MSG.replace(
        '{0}',
        `「${error.data.errors?.[0]?.columnName}」`
      );
    case COMMON_CONSTANT.IMPORT_MASTER_DATA_ERROR_CODE.AREA_INVALID_FORMAT:
      return COMMON_CONSTANT.IMPORT_MASTER_DATA_MESSAGE.COMMON_INVALID_FORMAT_MSG.replace(
        '{0}',
        `「${error.data.errors?.[0]?.columnName}」`
      );
    case COMMON_CONSTANT.IMPORT_MASTER_DATA_ERROR_CODE
      .COMMON_COLUMN_NOT_CORRECT:
      return COMMON_CONSTANT.IMPORT_MASTER_DATA_MESSAGE.COMMON_COLUMN_NOT_CORRECT_MSG.replace(
        '{0}',
        `「${error.data.errors?.[0]?.columnName}」`
      );
    case COMMON_CONSTANT.IMPORT_MASTER_DATA_ERROR_CODE.AREA_DATA_MISSING:
      return COMMON_CONSTANT.IMPORT_MASTER_DATA_MESSAGE.COMMON_DATA_MISSING_MSG.replace(
        '{0}',
        `「${error.data.errors?.[0]?.columnName}」`
      );
    case COMMON_CONSTANT.IMPORT_MASTER_DATA_ERROR_CODE.AREA_ALREADY_EXISTED:
      return COMMON_CONSTANT.IMPORT_MASTER_DATA_MESSAGE.COMMON_ALREADY_EXISTED_MSG.replace(
        '{0}',
        `「${error.data.errors?.[0]?.columnName}」`
      );
    case COMMON_CONSTANT.IMPORT_MASTER_DATA_ERROR_CODE.COMMON_IMPORT_EMPTY_FILE:
      return COMMON_CONSTANT.IMPORT_MASTER_DATA_MESSAGE
        .COMMON_IMPORT_EMPTY_FILE_MSG;
    default:
      return undefined;
  }
}

export function handleErrorCodeImportProcessingCenterFile(
  error: any
): string | undefined {
  const errorCode = error?.data?.errors?.[0]?.code;
  switch (errorCode) {
    case COMMON_CONSTANT.IMPORT_MASTER_DATA_ERROR_CODE
      .PROCESSING_CENTER_DUPLICATE:
      return COMMON_CONSTANT.IMPORT_MASTER_DATA_MESSAGE.COMMON_DUPLICATE_MSG;
    case COMMON_CONSTANT.IMPORT_MASTER_DATA_ERROR_CODE
      .PROCESSING_CENTER_INVALID_FORMAT:
      return COMMON_CONSTANT.IMPORT_MASTER_DATA_MESSAGE.COMMON_INVALID_FORMAT_MSG.replace(
        '{0}',
        `「${error.data.errors?.[0]?.columnName}」`
      );
    case COMMON_CONSTANT.IMPORT_MASTER_DATA_ERROR_CODE.COMPANY_NOT_EXIST:
      return COMMON_CONSTANT.IMPORT_MASTER_DATA_MESSAGE.COMMON_NOT_EXISTED_MSG.replace(
        '{0}',
        `「${error.data.errors?.[0]?.columnName}」`
      );
    case COMMON_CONSTANT.IMPORT_MASTER_DATA_ERROR_CODE.SECTOR_NOT_EXIST:
      return COMMON_CONSTANT.IMPORT_MASTER_DATA_MESSAGE.COMMON_NOT_EXISTED_MSG.replace(
        '{0}',
        `「${error.data.errors?.[0]?.columnName}」`
      );
    case COMMON_CONSTANT.IMPORT_MASTER_DATA_ERROR_CODE
      .COMMON_COLUMN_NOT_CORRECT:
      return COMMON_CONSTANT.IMPORT_MASTER_DATA_MESSAGE.COMMON_COLUMN_NOT_CORRECT_MSG.replace(
        '{0}',
        `「${error.data.errors?.[0]?.columnName}」`
      );
    case COMMON_CONSTANT.IMPORT_MASTER_DATA_ERROR_CODE
      .PROCESSING_CENTER_DATA_MISSING:
      return COMMON_CONSTANT.IMPORT_MASTER_DATA_MESSAGE.COMMON_DATA_MISSING_MSG.replace(
        '{0}',
        `「${error.data.errors?.[0]?.columnName}」`
      );
    case COMMON_CONSTANT.IMPORT_MASTER_DATA_ERROR_CODE.COMMON_IMPORT_EMPTY_FILE:
      return COMMON_CONSTANT.IMPORT_MASTER_DATA_MESSAGE
        .COMMON_IMPORT_EMPTY_FILE_MSG;
    default:
      return undefined;
  }
}

// Builds a "{0}" message replaced with the bracketed column name from the error payload
const formatImportColumnMessage = (template: string, error: any) =>
  template.replace('{0}', `「${error.data.errors?.[0]?.columnName}」`);

const USER_IMPORT_ERROR_MESSAGE_BUILDERS: Record<
  string,
  (error: any) => string
> = {
  [COMMON_CONSTANT.IMPORT_MASTER_DATA_ERROR_CODE.USER_DUPLICATE]: () =>
    COMMON_CONSTANT.IMPORT_MASTER_DATA_MESSAGE.COMMON_DUPLICATE_MSG,
  [COMMON_CONSTANT.IMPORT_MASTER_DATA_ERROR_CODE.USER_INVALID_FORMAT]: (
    error
  ) =>
    formatImportColumnMessage(
      COMMON_CONSTANT.IMPORT_MASTER_DATA_MESSAGE.COMMON_INVALID_FORMAT_MSG,
      error
    ),
  [COMMON_CONSTANT.IMPORT_MASTER_DATA_ERROR_CODE.STORE_NOT_EXIST]: (error) =>
    formatImportColumnMessage(
      COMMON_CONSTANT.IMPORT_MASTER_DATA_MESSAGE.COMMON_NOT_EXISTED_MSG,
      error
    ),
  [COMMON_CONSTANT.IMPORT_MASTER_DATA_ERROR_CODE.USER_ALREADY_EXISTED]: (
    error
  ) =>
    formatImportColumnMessage(
      COMMON_CONSTANT.IMPORT_MASTER_DATA_MESSAGE.COMMON_ALREADY_EXISTED_MSG,
      error
    ),
  [COMMON_CONSTANT.IMPORT_MASTER_DATA_ERROR_CODE.STORE_ALREADY_HAVE_MANAGER]:
    () => COMMON_CONSTANT.IMPORT_MASTER_DATA_MESSAGE.COMMON_DUPLICATE_MSG,
  [COMMON_CONSTANT.IMPORT_MASTER_DATA_ERROR_CODE.COMMON_COLUMN_NOT_CORRECT]: (
    error
  ) =>
    formatImportColumnMessage(
      COMMON_CONSTANT.IMPORT_MASTER_DATA_MESSAGE.COMMON_COLUMN_NOT_CORRECT_MSG,
      error
    ),
  [COMMON_CONSTANT.IMPORT_MASTER_DATA_ERROR_CODE.USER_DATA_MISSING]: (error) =>
    formatImportColumnMessage(
      COMMON_CONSTANT.IMPORT_MASTER_DATA_MESSAGE.COMMON_DATA_MISSING_MSG,
      error
    ),
  [COMMON_CONSTANT.IMPORT_MASTER_DATA_ERROR_CODE.COMMON_IMPORT_EMPTY_FILE]:
    () =>
      COMMON_CONSTANT.IMPORT_MASTER_DATA_MESSAGE.COMMON_IMPORT_EMPTY_FILE_MSG,
  [COMMON_CONSTANT.IMPORT_MASTER_DATA_ERROR_CODE
    .USER_PROCESSING_CENTER_NOT_EXISTED]: (error) =>
    formatImportColumnMessage(
      COMMON_CONSTANT.IMPORT_MASTER_DATA_MESSAGE.COMMON_NOT_EXISTED_MSG,
      error
    ),
  [COMMON_CONSTANT.VALIDATE_USER.STORE_MANAGER_ALREADY_EXISTED]: (error) =>
    formatImportColumnMessage(
      COMMON_CONSTANT.IMPORT_MASTER_DATA_MESSAGE.STOREMANAGER_ALREADY_EXISTED,
      error
    ),
  [COMMON_CONSTANT.IMPORT_MASTER_DATA_ERROR_CODE.STORE_FOR_USER_NOT_EXIST]: (
    error
  ) =>
    formatImportColumnMessage(
      COMMON_CONSTANT.IMPORT_MASTER_DATA_MESSAGE.COMMON_NOT_EXISTED_MSG,
      error
    ),
  [COMMON_CONSTANT.IMPORT_MASTER_DATA_ERROR_CODE.COMPANY_FOR_USER_NOT_EXIST]: (
    error
  ) =>
    formatImportColumnMessage(
      COMMON_CONSTANT.IMPORT_MASTER_DATA_MESSAGE.COMMON_NOT_EXISTED_MSG,
      error
    ),
  [COMMON_CONSTANT.IMPORT_MASTER_DATA_ERROR_CODE.SECTOR_FOR_USER_NOT_EXIST]: (
    error
  ) =>
    formatImportColumnMessage(
      COMMON_CONSTANT.IMPORT_MASTER_DATA_MESSAGE.COMMON_NOT_EXISTED_MSG,
      error
    ),
  [COMMON_CONSTANT.IMPORT_MASTER_DATA_ERROR_CODE.ROLE_NOT_EXIST]: (error) =>
    formatImportColumnMessage(
      COMMON_CONSTANT.IMPORT_MASTER_DATA_MESSAGE.COMMON_NOT_EXISTED_MSG,
      error
    ),
};

export function handleErrorCodeImportUserFile(error: any): string | undefined {
  const errorCode = error?.data?.errors?.[0]?.code;
  const buildMessage = USER_IMPORT_ERROR_MESSAGE_BUILDERS[errorCode];
  return buildMessage ? buildMessage(error) : undefined;
}

export function handleErrorCodeImportStoreFile(error: any): string | undefined {
  const errorCode = error?.data?.errors?.[0]?.code;
  switch (errorCode) {
    case COMMON_CONSTANT.IMPORT_MASTER_DATA_ERROR_CODE.STORE_DUPLICATE:
      return COMMON_CONSTANT.IMPORT_MASTER_DATA_MESSAGE.COMMON_DUPLICATE_MSG;
    case COMMON_CONSTANT.IMPORT_MASTER_DATA_ERROR_CODE.STORE_INVALID_FORMAT:
      return COMMON_CONSTANT.IMPORT_MASTER_DATA_MESSAGE.COMMON_INVALID_FORMAT_MSG.replace(
        '{0}',
        `「${error.data.errors?.[0]?.columnName}」`
      );
    case COMMON_CONSTANT.IMPORT_MASTER_DATA_ERROR_CODE.STORE_ALREADY_EXISTED:
      return COMMON_CONSTANT.IMPORT_MASTER_DATA_MESSAGE.COMMON_ALREADY_EXISTED_MSG.replace(
        '{0}',
        `「${error.data.errors?.[0]?.columnName}」`
      );
    case COMMON_CONSTANT.IMPORT_MASTER_DATA_ERROR_CODE.AREA_NOT_EXISTED:
      return COMMON_CONSTANT.IMPORT_MASTER_DATA_MESSAGE.COMMON_NOT_EXISTED_MSG.replace(
        '{0}',
        `「${error.data.errors?.[0]?.columnName}」`
      );
    case COMMON_CONSTANT.IMPORT_MASTER_DATA_ERROR_CODE
      .COMMON_COLUMN_NOT_CORRECT:
      return COMMON_CONSTANT.IMPORT_MASTER_DATA_MESSAGE.COMMON_COLUMN_NOT_CORRECT_MSG.replace(
        '{0}',
        `「${error.data.errors?.[0]?.columnName}」`
      );
    case COMMON_CONSTANT.IMPORT_MASTER_DATA_ERROR_CODE.STORE_DATA_MISSING:
      return COMMON_CONSTANT.IMPORT_MASTER_DATA_MESSAGE.COMMON_DATA_MISSING_MSG.replace(
        '{0}',
        `「${error.data.errors?.[0]?.columnName}」`
      );
    case COMMON_CONSTANT.IMPORT_MASTER_DATA_ERROR_CODE.COMMON_IMPORT_EMPTY_FILE:
      return COMMON_CONSTANT.IMPORT_MASTER_DATA_MESSAGE
        .COMMON_IMPORT_EMPTY_FILE_MSG;
    default:
      return undefined;
  }
}

export function handleErrorCodeImportCameraFile(
  error: any
): string | undefined {
  const errorCode = error?.data?.errors?.[0]?.code;
  switch (errorCode) {
    case COMMON_CONSTANT.IMPORT_MASTER_DATA_ERROR_CODE.CAMERA_DUPLICATE:
      return COMMON_CONSTANT.IMPORT_MASTER_DATA_MESSAGE.COMMON_DUPLICATE_MSG;
    case COMMON_CONSTANT.IMPORT_MASTER_DATA_ERROR_CODE.CAMERA_INVALID_FORMAT:
      return COMMON_CONSTANT.IMPORT_MASTER_DATA_MESSAGE.COMMON_INVALID_FORMAT_MSG.replace(
        '{0}',
        `「${error.data.errors?.[0]?.columnName}」`
      );
    case COMMON_CONSTANT.IMPORT_MASTER_DATA_ERROR_CODE.CAMERA_ALREADY_EXISTED:
      return COMMON_CONSTANT.IMPORT_MASTER_DATA_MESSAGE.COMMON_ALREADY_EXISTED_MSG.replace(
        '{0}',
        `「${error.data.errors?.[0]?.columnName}」`
      );
    case COMMON_CONSTANT.IMPORT_MASTER_DATA_ERROR_CODE
      .CAMERA_PROCESSING_CENTER_NOT_EXISTED:
      return COMMON_CONSTANT.IMPORT_MASTER_DATA_MESSAGE.COMMON_NOT_EXISTED_MSG.replace(
        '{0}',
        `「${error.data.errors?.[0]?.columnName}」`
      );
    case COMMON_CONSTANT.IMPORT_MASTER_DATA_ERROR_CODE
      .CAMERA_COMPANY_NOT_EXISTED:
      return COMMON_CONSTANT.IMPORT_MASTER_DATA_MESSAGE.COMMON_NOT_EXISTED_MSG.replace(
        '{0}',
        `「${error.data.errors?.[0]?.columnName}」`
      );
    case COMMON_CONSTANT.IMPORT_MASTER_DATA_ERROR_CODE.CAMERA_STORE_NOT_EXISTED:
      return COMMON_CONSTANT.IMPORT_MASTER_DATA_MESSAGE.COMMON_NOT_EXISTED_MSG.replace(
        '{0}',
        `「${error.data.errors?.[0]?.columnName}」`
      );
    case COMMON_CONSTANT.IMPORT_MASTER_DATA_ERROR_CODE
      .COMMON_COLUMN_NOT_CORRECT:
      return COMMON_CONSTANT.IMPORT_MASTER_DATA_MESSAGE.COMMON_COLUMN_NOT_CORRECT_MSG.replace(
        '{0}',
        `「${error.data.errors?.[0]?.columnName}」`
      );
    case COMMON_CONSTANT.IMPORT_MASTER_DATA_ERROR_CODE.CAMERA_DATA_MISSING:
      return COMMON_CONSTANT.IMPORT_MASTER_DATA_MESSAGE.COMMON_DATA_MISSING_MSG.replace(
        '{0}',
        `「${error.data.errors?.[0]?.columnName}」`
      );
    case COMMON_CONSTANT.IMPORT_MASTER_DATA_ERROR_CODE.COMMON_IMPORT_EMPTY_FILE:
      return COMMON_CONSTANT.IMPORT_MASTER_DATA_MESSAGE
        .COMMON_IMPORT_EMPTY_FILE_MSG;
    default:
      return undefined;
  }
}

export function findFirstDuplicateIPAndDataIndex(data: any) {
  const ipSet = new Set();
  for (let i = 0; i < data.length; i++) {
    const currentItem = data[i];
    // Check if IP is duplicate
    if (ipSet.has(currentItem.ip)) {
      return i; // Return the index of the first duplicate data item
    }
    // Add the current IP to the set
    ipSet.add(currentItem.ip);
  }
  return -1; // Return -1 if no duplicates are found
}
